import { Beaker, FlaskConical, Zap } from 'lucide-react';
import EquipmentAdminValidation from '@/components/EquipmentAdminValidation';

export default function AdminPHValidation() {
  return (
    <EquipmentAdminValidation
      title="pH Adjustment Validation — Admin Audit"
      description="Acid/base neutralization with buffer capacity, Camp-Stein mixing, reagent dosing, and effluent pH target."
      buildSections={(p) => {
        const flowRate = Number(p.flowRate ?? 350);
        const influentpH = Number(p.ph ?? 6.2);
        const targetpH = 7.0;
        const alkalinity = Number(p.alkalinity ?? 800);
        const temperature = Number(p.temp ?? 25);
        const safetyFactor = 1.3;
        const reagentType = 'naoh';
        const reagentConcentration = 30;
        const mixerPower = 1.5;
        const mixerRPM = 60;
        const mixerDiameter = 0.6;
        const tankDiameter = 2.5;
        const tankDepth = 2.5;
        const freeboard = 0.5;

        const q = flowRate / 24;
        const tempK = temperature + 273.15;
        const mu = 0.001 * Math.pow(10, 247.8 / tempK - 140);
        const rhoWater = 998 - 0.2 * (temperature - 20);
        const area = Math.PI * Math.pow(tankDiameter / 2, 2);
        const volume = area * tankDepth;
        const totalVolume = area * (tankDepth + freeboard);
        const detentionTime = volume / q;
        const detentionMin = detentionTime * 60;
        const phDiff = Math.abs(targetpH - influentpH);
        const bufferCapacity = alkalinity / 50;
        const chemicalDemand = phDiff * bufferCapacity * safetyFactor;
        const chemFactor = 1.25;
        const chemicalDose = chemicalDemand / chemFactor;
        const dailyChemical = (flowRate * chemicalDose) / 1000;
        const chemicalVolume = dailyChemical / (reagentConcentration * 10);
        const mixingEfficiency = mixerPower > 1.5 ? 0.95 : mixerPower > 0.8 ? 0.90 : mixerPower > 0.3 ? 0.85 : 0.70;
        const detentionEfficiency = detentionMin >= 20 ? 0.98 : detentionMin >= 10 ? 0.93 : detentionMin >= 5 ? 0.85 : 0.70;
        const reactionCompleteness = mixingEfficiency * detentionEfficiency;
        const actualEffluentPH = influentpH + (targetpH - influentpH) * reactionCompleteness;
        const phDeviation = Math.abs(actualEffluentPH - targetpH);
        const withinCompliance = phDeviation <= 0.5;
        const mixerPowerW = mixerPower * 1000;
        const gValue = mixerPower > 0 ? Math.sqrt(mixerPowerW / (mu * volume)) : 0;
        const tipSpeed = mixerRPM > 0 ? (Math.PI * mixerDiameter * mixerRPM) / 60 : 0;
        const mixerPowerPerVolume = volume > 0 ? mixerPower / volume : 0;

        const fmt = (v: any, d = 2) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

        return [
          {
            title: 'Tank Physics & Retention',
            icon: Beaker,
            equations: [
              { label: 'Flow rate (hourly)', formula: 'q = flowRate / 24', substitution: `q = ${fmt(flowRate, 0)} / 24 = ${fmt(q, 1)} m³/h`, result: fmt(q, 1), status: 'pass', unit: 'm³/h' },
              { label: 'Tank area', formula: 'A = π×(D/2)²', substitution: `A = π×(${tankDiameter}/2)² = ${fmt(area, 2)} m²`, result: fmt(area, 2), status: 'pass', unit: 'm²' },
              { label: 'Working volume', formula: 'V = A × depth', substitution: `V = ${fmt(area, 2)} × ${tankDepth} = ${fmt(volume, 1)} m³`, result: fmt(volume, 1), status: 'pass', unit: 'm³' },
              { label: 'Total volume', formula: 'V_total = A × (depth + freeboard)', substitution: `V_total = ${fmt(area, 2)} × (${tankDepth} + ${freeboard}) = ${fmt(totalVolume, 1)} m³`, result: fmt(totalVolume, 1), status: 'pass', unit: 'm³' },
              { label: 'Retention time', formula: 'τ = V/q × 60', substitution: `τ = ${fmt(volume, 1)}/${fmt(q, 1)} × 60 = ${fmt(detentionMin, 0)} min`, result: fmt(detentionMin, 0), status: detentionMin >= 15 ? 'pass' : detentionMin >= 8 ? 'warn' : 'fail', unit: 'min' },
              { label: 'Water viscosity', formula: 'μ = 0.001 × 10^(247.8/T_K − 140)', substitution: `μ = 0.001 × 10^(247.8/${fmt(tempK, 1)} − 140) = ${fmt(mu * 1000, 3)} mPa·s`, result: fmt(mu * 1000, 3), status: 'pass', unit: 'mPa·s' },
            ],
          },
          {
            title: 'Neutralization Chemistry',
            icon: FlaskConical,
            equations: [
              { label: 'pH change', formula: 'ΔpH = |target − influent|', substitution: `ΔpH = |${targetpH} − ${fmt(influentpH, 1)}| = ${fmt(phDiff, 1)}`, result: fmt(phDiff, 1), status: 'pass', unit: '' },
              { label: 'Buffer capacity', formula: 'BC = Alkalinity / 50', substitution: `BC = ${alkalinity} / 50 = ${fmt(bufferCapacity, 1)}`, result: fmt(bufferCapacity, 1), status: 'pass', unit: '' },
              { label: 'Chemical demand', formula: 'N = ΔpH × BC × SF', substitution: `N = ${fmt(phDiff, 1)} × ${fmt(bufferCapacity, 1)} × ${safetyFactor} = ${fmt(chemicalDemand, 0)} mg/L CaCO₃`, result: fmt(chemicalDemand, 0), status: 'pass', unit: 'mg/L' },
              { label: 'NaOH dose', formula: 'Dose = N / 1.25', substitution: `Dose = ${fmt(chemicalDemand, 0)} / 1.25 = ${fmt(chemicalDose, 1)} mg/L`, result: fmt(chemicalDose, 1), status: 'pass', unit: 'mg/L' },
              { label: 'Daily consumption', formula: 'kg/day = flowRate × Dose / 1000', substitution: `${fmt(flowRate, 0)} × ${fmt(chemicalDose, 1)} / 1000 = ${fmt(dailyChemical, 1)} kg/day`, result: fmt(dailyChemical, 1), status: 'pass', unit: 'kg/day' },
              { label: 'Solution volume', formula: 'V = kg/day / (Conc% × 10)', substitution: `V = ${fmt(dailyChemical, 1)} / (${reagentConcentration} × 10) = ${fmt(chemicalVolume * 1000, 1)} L/day`, result: fmt(chemicalVolume * 1000, 1), status: 'pass', unit: 'L/day' },
              { label: 'Mixing efficiency', formula: 'η_mix = f(P_mixer)', substitution: `P = ${mixerPower} kW → η_mix = ${fmt(mixingEfficiency, 2)}`, result: fmt(mixingEfficiency, 2), status: mixingEfficiency >= 0.90 ? 'pass' : 'warn', unit: '' },
              { label: 'Detention efficiency', formula: 'η_det = f(τ)', substitution: `τ = ${fmt(detentionMin, 0)} min → η_det = ${fmt(detentionEfficiency, 2)}`, result: fmt(detentionEfficiency, 2), status: detentionEfficiency >= 0.93 ? 'pass' : 'warn', unit: '' },
              { label: 'Reaction completeness', formula: 'η = η_mix × η_det', substitution: `η = ${fmt(mixingEfficiency, 2)} × ${fmt(detentionEfficiency, 2)} = ${fmt(reactionCompleteness, 2)}`, result: fmt(reactionCompleteness, 2), status: reactionCompleteness >= 0.90 ? 'pass' : reactionCompleteness >= 0.80 ? 'warn' : 'fail', unit: '' },
              { label: 'Estimated effluent pH', formula: 'pH_out = pH_in + ΔpH × η', substitution: `pH_out = ${fmt(influentpH, 1)} + ${fmt(phDiff, 1)} × ${fmt(reactionCompleteness, 2)} = ${fmt(actualEffluentPH, 1)}`, result: fmt(actualEffluentPH, 1), status: withinCompliance ? 'pass' : 'warn', unit: '' },
            ],
          },
          {
            title: 'Mixer — Camp-Stein',
            icon: Zap,
            equations: [
              { label: 'G-value', formula: 'G = √(P/μV)', substitution: `G = √(${mixerPowerW} / (${fmt(mu, 5)} × ${fmt(volume, 1)})) = ${fmt(gValue, 0)} s⁻¹`, result: fmt(gValue, 0), status: gValue >= 20 ? 'pass' : gValue >= 10 ? 'warn' : 'fail', unit: 's⁻¹' },
              { label: 'Tip speed', formula: 'v = π×D×N/60', substitution: `v = π×${mixerDiameter}×${mixerRPM}/60 = ${fmt(tipSpeed, 1)} m/s`, result: fmt(tipSpeed, 1), status: 'pass', unit: 'm/s' },
              { label: 'Power per volume', formula: 'P/V', substitution: `${mixerPower} / ${fmt(volume, 1)} = ${fmt(mixerPowerPerVolume, 3)} kW/m³`, result: fmt(mixerPowerPerVolume, 3), status: 'pass', unit: 'kW/m³' },
            ],
          },
        ];
      }}
    />
  );
}
